import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import {
  EstimateInvalidError,
  EstimateRefusedError,
  useEstimateActions,
  type Estimate,
} from '@/routes/estimates/useEstimates';

/**
 * The office's acts on an estimate (M2-05's endpoints, given a screen by
 * M2-06; Kyle, 2026-09-26): send it, record the customer's answer, revise it,
 * delete a draft. Rendered only for `customers.manage` — a technician drafts,
 * the office sends — and each button only in the status the API accepts it
 * in, so the screen never offers what the server is bound to refuse. The
 * server still decides: a refusal comes back as its own sentence.
 */
export function EstimateActs({
  estimate,
  draftSettled,
  onAnnounce,
}: {
  estimate: Estimate;
  /** For a draft: the builder's lines are all saved. Send waits for it (see EstimateBuilder). */
  draftSettled: boolean;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = useId();
  const acts = useEstimateActions(estimate.id);
  const [expiresOn, setExpiresOn] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const pending = Object.values(acts).some((act) => act.isPending);

  function explain(error: unknown) {
    setProblem(
      error instanceof EstimateRefusedError || error instanceof EstimateInvalidError
        ? t('estimates.acts.refused', { reason: error.message })
        : t('estimates.acts.failed'),
    );
  }

  const run = <T,>(
    mutate: (options: { onSuccess: (value: T) => void; onError: (e: unknown) => void }) => void,
    done: (value: T) => void,
  ) => {
    setProblem(null);
    mutate({ onSuccess: done, onError: explain });
  };

  const status = estimate.status;

  return (
    <section
      aria-labelledby={`${id}-heading`}
      className="space-y-3 rounded-lg border border-[var(--color-border)] p-4"
    >
      <h2 id={`${id}-heading`} className="text-base font-semibold">
        {t('estimates.acts.heading')}
      </h2>

      {status === 'Draft' ? (
        <div className="space-y-3">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {t('estimates.acts.sendHelp')}
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="block text-sm">
              <span className="mb-1 block font-medium">{t('estimates.acts.expiresOn')}</span>
              <input
                type="date"
                className={FIELD_CLASS}
                value={expiresOn}
                aria-describedby={`${id}-expires-hint`}
                onChange={(event) => setExpiresOn(event.target.value)}
              />
            </label>
            <Button
              type="button"
              disabled={pending || !draftSettled}
              aria-describedby={draftSettled ? undefined : `${id}-wait`}
              onClick={() =>
                run(
                  (o) => acts.send.mutate(expiresOn === '' ? null : expiresOn, o),
                  () => onAnnounce(t('estimates.acts.sent')),
                )
              }
            >
              {acts.send.isPending ? t('estimates.acts.sending') : t('estimates.acts.send')}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={pending}
              onClick={() => setConfirmingDelete(true)}
            >
              {t('estimates.acts.delete')}
            </Button>
          </div>
          <p id={`${id}-expires-hint`} className="text-xs text-[var(--color-muted-foreground)]">
            {t('estimates.acts.expiresHint')}
          </p>
          {draftSettled ? null : (
            <p id={`${id}-wait`} className="text-xs text-[var(--color-muted-foreground)]">
              {t('estimates.acts.waitForSave')}
            </p>
          )}
        </div>
      ) : null}

      {status === 'Sent' ? (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            disabled={pending}
            onClick={() =>
              run(
                (o) => acts.approve.mutate(undefined, o),
                () => onAnnounce(t('estimates.acts.approved')),
              )
            }
          >
            {t('estimates.acts.approve')}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() =>
              run(
                (o) => acts.decline.mutate(undefined, o),
                () => onAnnounce(t('estimates.acts.declined')),
              )
            }
          >
            {t('estimates.acts.decline')}
          </Button>
        </div>
      ) : null}

      {status === 'Sent' || status === 'Expired' || status === 'Declined' ? (
        <Button
          type="button"
          variant="outline"
          disabled={pending}
          onClick={() =>
            run(
              (o) => acts.revise.mutate(undefined, o),
              (revision: Estimate) => {
                onAnnounce(t('estimates.acts.revised', { revision: revision.revision }));
                navigate(`/estimates/${revision.id}`);
              },
            )
          }
        >
          {t('estimates.acts.revise')}
        </Button>
      ) : null}

      {problem ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {problem}
        </p>
      ) : null}

      {confirmingDelete ? (
        <ConfirmDialog
          titleId={`${id}-delete-title`}
          title={t('estimates.acts.deleteTitle')}
          description={t('estimates.acts.deleteDescription')}
          confirmLabel={t('estimates.acts.deleteConfirm')}
          destructive
          isPending={acts.remove.isPending}
          errorMessage={problem ?? undefined}
          onConfirm={() =>
            run(
              (o) => acts.remove.mutate(undefined, o),
              () => navigate(estimate.jobId ? `/jobs/${estimate.jobId}` : '/estimates'),
            )
          }
          onClose={() => setConfirmingDelete(false)}
        />
      ) : null}
    </section>
  );
}
